import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Card, Container, FormLabel } from 'react-bootstrap';
import { formatPrice } from '../../utils/utils';
import GenericList, { GenericListItem } from '../GenericList';
import NumberInput from '../NumberInput';
import Radio from '../Radio';

export type PricingCardListItem = {
  text: string;
  value: number | undefined | null;
};

export type customPricingCalculatorCardProps = React.PropsWithoutRef<{
  className?: string;
  selected?: boolean;
  title: string;
  subtitle?: string | React.ReactNode;
  listItems: Array<PricingCardListItem>;
  handleChange: Function;
  resultText?: string;
  radioButtonName?: string;
  radioButtonValue: string | number;
  radioButtonDefaultChecked?: boolean;
  radioButtonDisabled?: boolean;
  resultOnly?: boolean;
  isInvalid?: boolean;
  prePaidCalculation?: boolean;
  errorText?: string;
  isCollapsedView?: boolean;
  disableNumberInputField?: boolean;
  isPerPayPeriod?: boolean;
  minInputValue?: string;
  maxInputValue?: string;
  alwaysShowSubtitle?: boolean;
  disabled?: boolean;
}>;

const PricingCalculatorCard = ({
  className,
  selected = false,
  title,
  subtitle,
  listItems,
  handleChange,
  resultText = 'Total Contributions',
  radioButtonName,
  radioButtonValue,
  radioButtonDefaultChecked = false,
  radioButtonDisabled = false,
  isCollapsedView = false,
  resultOnly = false,
  isInvalid = false,
  errorText = '',
  prePaidCalculation = true,
  disableNumberInputField = false,
  isPerPayPeriod = false,
  alwaysShowSubtitle,
  minInputValue,
  maxInputValue,
  disabled
}: customPricingCalculatorCardProps) => {
  const [userInputValue, setUserInputValue] = useState<
    number | undefined | null
  >(listItems[0].value);
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedView);
  const isMounted = useRef(false);
  const handleChangeUserInputValue = (value: number | undefined | null) => {

    
    setUserInputValue(value);
    if (!value || value > -1) {
      handleChange(value);
    }
  };

  const getUserInputInteger = () => {
    return userInputValue ? userInputValue : 0;
  };

  const calculateTotal = () => {
    let total = 0;
    if (userInputValue === 0) return formatPrice(total);
    listItems.forEach((item, index) => {
      if (item.value) {
        if (isPerPayPeriod && !index) {
          total += (item.value*12);
        } else {
          total += item.value;
        }
      }
    });
    return formatPrice(total);
  };

  useEffect(() => {
    if (isMounted.current) {
      if (selected && !isCollapsedView) {
        setIsCollapsed(false);
      }

      if (!selected && isCollapsedView) {
        setIsCollapsed(true);
      }
    } else {
      isMounted.current = true;
    }
  }, [selected, isCollapsedView]);

  return (
    <Container
      className={`pricing-calc-card ${className} ${selected ? 'selected' : ''}
        ${resultOnly ? 'selected' : ''}`}
    >
      <FormLabel>
        <Card>
          <Card.Body>
            {!resultOnly && (
              <div className="pricing-calc-heading-group">
                <div
                  className={`pricing-calc-title-group ${
                    isCollapsed ? 'align-items-center' : ''
                  }`}
                >
                  <Radio
                    name={radioButtonName}
                    value={radioButtonValue}
                    checked={radioButtonDefaultChecked}
                    disabled={radioButtonDisabled}
                  />
                  <div className="pricing-calc-title">
                    <Card.Title className={'mb-0'}>{title}</Card.Title>
                    {(!isCollapsed || alwaysShowSubtitle) && (
                      <Card.Subtitle className="mt-lg-1">
                        {subtitle}
                      </Card.Subtitle>
                    )}
                  </div>
                </div>
                {isCollapsed && disableNumberInputField && <div
                  className='mb-0'
                >
                  <h4
                    className={`${disabled ? 'disabled' : ''} invalid-price mb-0`}>
                    {`${formatPrice(userInputValue || 0)}`}
                  </h4>
                  {!disabled && <Card.Subtitle className="mt-lg-1 text-end">
                    per-pay-period
                  </Card.Subtitle>}
                </div>}
                {!isCollapsed && (
                  <div className="pricing-calc-input-group">
                    {!disableNumberInputField && <NumberInput
                      type="number"
                      icon={light('dollar')}
                      value={userInputValue || 0}
                      onChange={handleChangeUserInputValue}
                      disabled={!selected}
                      isInvalid={getUserInputInteger() < parseInt(minInputValue || '0', 10) || getUserInputInteger() > parseInt(maxInputValue || '1000000', 10)}
                    />}
                    {disableNumberInputField && <div
                      className='mb-4'
                    >
                      <h4
                        className="invalid-price mb-0">
                        {`${formatPrice(userInputValue || 0)}`}
                      </h4>
                      <Card.Subtitle className="mt-lg-1">per-pay-period</Card.Subtitle>
                    </div>}
                    {prePaidCalculation && (
                      <Card.Subtitle>
                        {isPerPayPeriod ? "Annual contribution" : "Per-pay-period contribution"}
                        <span>
                          <b>{formatPrice(isPerPayPeriod ? getUserInputInteger() * 12 : getUserInputInteger() / 12)}</b>
                        </span>
                      </Card.Subtitle>
                    )}
                  </div>
                )}
              </div>
            )}
            {listItems.length > 1 && (
              <Fragment>
                {!isCollapsed && (
                  <div
                    className={`pricing-card-result-breakdown ${
                      resultOnly ? 'result-only' : ''
                    }`}
                  >
                    <GenericList>
                      {listItems.map((item, index) => (
                        <GenericListItem
                          price={
                            item.value ? isPerPayPeriod && !index ? item.value*12 : userInputValue ? item.value : 0 : 0
                          }
                          key={index}
                        >
                          {item.text}
                        </GenericListItem>
                      ))}
                    </GenericList>
                  </div>
                )}
              </Fragment>
            )}
            {!isCollapsed && (
              <div
                className={`pricing-card-result ${
                  resultOnly ? 'result-only' : ''
                } ${isInvalid ? 'invalid' : ''}`}
              >
                <div className={'d-flex w-100 justify-content-between'}>
                  <div className={'result-title'}>{resultText}</div>
                  <div className="invalid-price">{`${calculateTotal()}`}</div>
                </div>
                {selected && errorText && (
                  <div className="mt-3">
                    <p className="m-0 invalid-msg">{errorText}</p>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </FormLabel>
    </Container>
  );
};

export default PricingCalculatorCard;
